/**
 * Shared sidebar search hook — extracted from FeedClient.
 * Debounced search across people, communities, tags.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

import { fetchWithAuth } from '../../../lib/fetchWithAuth';
export type SearchType = 'people' | 'communities' | 'tags';

export interface SearchPerson {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  followers: number;
}

export interface SearchCommunity {
  id: string;
  name: string;
  nameSlug?: string;
  avatar?: string;
  memberCount: number;
}

export interface SearchTag {
  tag: string;
  count: number;
}

export function useSidebarSearch() {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('people');
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [people, setPeople] = useState<SearchPerson[]>([]);
  const [communities, setCommunities] = useState<SearchCommunity[]>([]);
  const [tags, setTags] = useState<SearchTag[]>([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setPeople([]);
      setCommunities([]);
      setTags([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);
    const timer = setTimeout(async () => {
      try {
        if (searchType === 'people') {
          const res = await fetchWithAuth(`/api/search/users?q=${encodeURIComponent(query)}`, { credentials: 'include' });
          if (res.ok && mountedRef.current) {
            const data = await res.json();
            setPeople((data.users || data || []).map((u: any) => ({
              id: u.id?.toString(),
              username: u.username,
              displayName: u.profileName || u.displayName || u.name || u.username,
              avatar: u.profilePicture || u.image || u.avatar,
              followers: u.followerCount || u._count?.followers || 0,
            })));
          }
        } else if (searchType === 'communities') {
          const res = await fetchWithAuth(`/api/search/communities?q=${encodeURIComponent(query)}`, { credentials: 'include' });
          if (res.ok && mountedRef.current) {
            const data = await res.json();
            setCommunities((data.communities || data || []).map((c: any) => ({
              id: c.id?.toString(),
              name: c.name,
              nameSlug: c.nameSlug || c.slug,
              avatar: c.image || c.avatar,
              memberCount: c.memberCount || c._count?.members || 0,
            })));
          }
        } else if (searchType === 'tags') {
          const res = await fetchWithAuth(`/api/search/tags?q=${encodeURIComponent(query)}`, { credentials: 'include' });
          if (res.ok && mountedRef.current) {
            const data = await res.json();
            setTags((data.tags || data || []).map((t: any) => ({
              tag: typeof t === 'string' ? t : t.tag || t.name,
              count: t.count || t.postCount || 0,
            })));
          }
        }
      } catch { /* silent */ }
      finally { if (mountedRef.current) setIsSearching(false); }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, searchType]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setShowResults(false);
    setPeople([]);
    setCommunities([]);
    setTags([]);
  }, []);

  return {
    query,
    setQuery,
    searchType,
    setSearchType,
    showResults,
    setShowResults,
    isSearching,
    people,
    communities,
    tags,
    clearSearch,
  };
}
